import { Mail, ExternalLink, MapPin, Building2 } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function Contacto() {
  return (
    <div className="min-h-screen pt-20" data-testid="contacto-page">
      <Helmet>
        <title>Contacto | SOSTRE BALEAR</title>
        <meta name="description" content="Contacta con PASTORIA PROJECT SL, entidad redactora del estudio SOSTRE BALEAR. Email: info@pastoria.es. Consultoría en desarrollo territorial y vivienda." />
        <link rel="canonical" href="https://www.sostrebalear.com/contacto" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contacto | SOSTRE BALEAR" />
        <meta property="og:description" content="Contacta con PASTORIA PROJECT SL para información sobre el estudio SOSTRE BALEAR." />
        <meta property="og:url" content="https://www.sostrebalear.com/contacto" />
        <meta property="og:image" content="https://www.sostrebalear.com/assets/logos/sostre-balear.png" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="SOSTRE BALEAR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contacto | SOSTRE BALEAR" />
        <meta name="twitter:description" content="Contacta con PASTORIA PROJECT SL para información sobre el estudio SOSTRE BALEAR." />
      </Helmet>
      {/* Contact Section - Combined */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-slate-100 p-8 md:p-12 shadow-sm">
            <span className="text-sm font-medium tracking-wide uppercase text-[#22c55e]">
              Información
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 tracking-tight">
              Contacto
            </h1>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Para más información sobre el estudio SOSTRE BALEAR, incluyendo
              documentación detallada sobre cada uno de los entregables del
              proyecto, así como servicios adicionales de consultoría
              especializada, puede contactar con los responsables del proyecto.
            </p>

            <hr className="my-8 border-slate-200" />

            {/* PASTORIA Info */}
            <div className="flex items-center gap-4 mb-4">
              <img
                src="/assets/logos/pastoria.png"
                alt="PASTORIA"
                className="h-12 w-auto"
              />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              PASTORIA PROJECT SL
            </h3>
            <p className="text-sm text-[#22c55e] font-medium mb-4">
              Entidad redactora del estudio
            </p>
            <p className="text-slate-600 mb-6">
              Consultora especializada en proyectos de desarrollo territorial
              y vivienda.
            </p>
            <div className="space-y-4">
              <a
                href="mailto:info@pastoria.es"
                className="flex items-center gap-3 text-slate-600 hover:text-[#22c55e] transition-colors"
                data-testid="link-email"
              >
                <div className="w-10 h-10 bg-[#22c55e]/10 rounded-full flex items-center justify-center">
                  <Mail size={18} className="text-[#22c55e]" />
                </div>
                <div>
                  <span className="text-sm text-slate-500">Email</span>
                  <p className="font-medium">info@pastoria.es</p>
                </div>
              </a>
              <a
                href="https://www.pastoria.es"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-slate-600 hover:text-[#22c55e] transition-colors"
                data-testid="link-pastoria-web"
              >
                <div className="w-10 h-10 bg-[#22c55e]/10 rounded-full flex items-center justify-center">
                  <ExternalLink size={18} className="text-[#22c55e]" />
                </div>
                <div>
                  <span className="text-sm text-slate-500">Web</span>
                  <p className="font-medium">www.pastoria.es</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Collaborators */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
            Entidades Colaboradoras
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <a
              href="https://www.caib.es/webgoib/w/conselleria-turisme-cultura-i-esports"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-100 transition-all"
            >
              <img
                src="/assets/logos/aetib.png"
                alt="AETIB"
                className="h-16 w-auto grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all"
              />
            </a>
            <a
              href="https://clusterteib.es/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-100 transition-all"
            >
              <img
                src="/assets/logos/teib.jpg"
                alt="TEIB"
                className="h-16 w-auto grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all"
              />
            </a>
            <a
              href="https://www.linkedin.com/company/m2f-advisors/about/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-100 transition-all"
            >
              <img
                src="/assets/logos/m2f.jpg"
                alt="M2F Advisors"
                className="h-16 w-auto grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Location Context */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-slate-500 mb-4">
            <MapPin size={20} />
            <span>Illes Balears, España</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Un proyecto para las Illes Balears
          </h2>
          <p className="text-slate-600 leading-relaxed">
            SOSTRE BALEAR nace de la necesidad de dar respuesta a la crisis
            habitacional que afecta a las Illes Balears, proponiendo un modelo
            innovador de colaboración público-privada para proporcionar vivienda
            asequible a los trabajadores del sector turístico.
          </p>
        </div>
      </section>
    </div>
  );
}
